import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { PermissionChangeRequest, FolderPermissionResponse } from '../../../api';
import {
  deleteApiFolderPermissionsByGraphFolderIdRemoveMutation,
  postApiFolderPermissionsByGraphFolderIdAddMutation,
  putApiFolderPermissionsByGraphFolderIdUpdateMutation,
} from '../../../api/@tanstack/react-query.gen';

interface UsePermissionOperationsProps {
  graphFolderId: string;
  // Required: every permission endpoint needs it to address the folder in Exchange.
  exchangeFolderId: string;
  refetch: () => Promise<unknown>;
}

export function useFolderPermissionsViewOperations({ graphFolderId, exchangeFolderId, refetch }: UsePermissionOperationsProps) {
  const [isBusy, setIsBusy] = useState(false);

  const addFolderPermissions = useMutation({
    ...postApiFolderPermissionsByGraphFolderIdAddMutation(),
  });

  const editFolderPermissions = useMutation({
    ...putApiFolderPermissionsByGraphFolderIdUpdateMutation(),
  });

  const deleteFolderPermissions = useMutation({
    ...deleteApiFolderPermissionsByGraphFolderIdRemoveMutation(),
  });

  const handleAdd = async (permissionsToChange: PermissionChangeRequest, applyRecursive: boolean) => {
    setIsBusy(true);
    try {
      await addFolderPermissions.mutateAsync({
        path: { graphFolderId },
        body: {
          changePermissionsInput: permissionsToChange,
          exchangeFolderId,
          applyRecursive,
        },
      });
      await refetch();
    } finally {
      setIsBusy(false);
    }
  };

  const handleEdit = async (permissionsToChange: PermissionChangeRequest, applyRecursive: boolean) => {
    setIsBusy(true);
    try {
      await editFolderPermissions.mutateAsync({
        path: { graphFolderId },
        body: {
          changePermissionsInput: permissionsToChange,
          exchangeFolderId,
          applyRecursive,
        },
      });
      await refetch();
    } finally {
      setIsBusy(false);
    }
  };

  const handleDelete = async (permissionsToDelete: FolderPermissionResponse, applyRecursive: boolean) => {
    if (!permissionsToDelete.email) {
      throw new Error('There is no email assigned to the user. Please assign an email.');
    }

    setIsBusy(true);
    try {
      await deleteFolderPermissions.mutateAsync({
        path: { graphFolderId },
        body: {
          exchangeFolderId,
          targetUserEmail: permissionsToDelete.email,
          applyRecursive,
        },
      });
      await refetch();
    } finally {
      setIsBusy(false);
    }
  };

  return {
    handleAdd,
    handleEdit,
    handleDelete,
    isLoading: isBusy,
    error: addFolderPermissions.error || editFolderPermissions.error || deleteFolderPermissions.error,
  };
}
